import { useRef, useState } from 'react';
import { useTranslation } from '@/i18n';
import { useCardBackground } from '@/hooks-v2/aggregator';
import {
  isBackgroundType,
  MAX_BACKGROUND_BYTES,
  readFileAsBase64,
} from '@/utils/card-background-file';
import type {
  CardBackgroundControlProps,
  CardBackgroundFeedback,
} from '@/types/card-balances.types';

export default function CardBackgroundControl({
  merchantId,
  cardId,
  revision,
  imageUrl,
  idPrefix,
  onChanged,
}: CardBackgroundControlProps): React.JSX.Element {
  const { t } = useTranslation();
  const { isBusy, upload, remove } = useCardBackground(merchantId, cardId);
  const [feedback, setFeedback] = useState<CardBackgroundFeedback>(null);
  const [failedPreview, setFailedPreview] = useState(false);
  const input = useRef<HTMLInputElement>(null);

  const fail = (error: unknown): void => {
    const code = typeof error === 'object' && error !== null && 'code' in error ? error.code : null;
    setFeedback(code === 'CONFLICT' ? 'conflict' : 'failed');
  };

  const choose = async (file: File | undefined): Promise<void> => {
    if (file === undefined || isBusy) return;
    if (!isBackgroundType(file.type) || file.size > MAX_BACKGROUND_BYTES) {
      setFeedback('invalidFile');
      return;
    }
    setFeedback(null);
    try {
      await upload({
        content_type: file.type,
        image_base64: await readFileAsBase64(file),
        expected_revision: revision,
      });
      setFailedPreview(false);
      setFeedback('saved');
      onChanged();
    } catch (error: unknown) {
      fail(error);
    } finally {
      if (input.current !== null) input.current.value = '';
    }
  };

  const clear = async (): Promise<void> => {
    if (isBusy) return;
    setFeedback(null);
    try {
      await remove(revision);
      setFeedback('removed');
      onChanged();
    } catch (error: unknown) {
      fail(error);
    }
  };

  return (
    <section className="mt-4 space-y-2 border-t pt-4" aria-labelledby={`${idPrefix}-background-heading`}>
      <h4 id={`${idPrefix}-background-heading`} className="font-medium text-gray-900">
        {t('merchant.memberList.cardBackground.title')}
      </h4>
      {imageUrl !== null && !failedPreview ? (
        <img
          src={imageUrl}
          alt={t('merchant.memberList.cardBackground.previewAlt')}
          onError={() => setFailedPreview(true)}
          className="h-28 w-full rounded border object-cover"
        />
      ) : (
        <p className="text-sm text-gray-600">
          {imageUrl !== null
            ? t('merchant.memberList.cardBackground.previewFailed')
            : t('merchant.memberList.cardBackground.none')}
        </p>
      )}
      <label htmlFor={`${idPrefix}-background-file`} className="block text-sm font-medium">
        {imageUrl === null
          ? t('merchant.memberList.cardBackground.upload')
          : t('merchant.memberList.cardBackground.replace')}
      </label>
      <input
        id={`${idPrefix}-background-file`}
        ref={input}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        disabled={isBusy}
        onChange={(event) => void choose(event.target.files?.[0])}
        className="block w-full text-sm"
      />
      <p className="text-xs text-gray-500">{t('merchant.memberList.cardBackground.help')}</p>
      {imageUrl !== null && (
        <button
          type="button"
          disabled={isBusy}
          onClick={() => void clear()}
          className="text-sm text-red-600 disabled:opacity-50"
        >
          {t('merchant.memberList.cardBackground.remove')}
        </button>
      )}
      {feedback === 'saved' || feedback === 'removed' ? (
        <p role="status" className="text-green-700">
          {t(`merchant.memberList.cardBackground.${feedback}`)}
        </p>
      ) : feedback !== null ? (
        <p role="alert" className="text-red-700">
          {t(`merchant.memberList.cardBackground.${feedback}`)}
        </p>
      ) : null}
    </section>
  );
}
